import { StatusBadge } from '@chiroup/components/StatusBadge';
import { SeverityEnum } from '@chiroup/components/enums/severity';
import { ColumnDef, DataTableColumnHeader } from '@chiroup/components/ui/data-table';
import { ReferralAdminRow, ReferralDisplayStatus } from '@chiroup/core/types/Referral.type';
import dayjs from 'dayjs';
import React from 'react';
import { Link } from 'react-router-dom';

export const REFERRAL_STATUS_META: Record<
  ReferralDisplayStatus,
  { label: string; severity: SeverityEnum }
> = {
  trial: { label: 'Trial', severity: SeverityEnum.Info },
  closeWon: { label: 'Close Won', severity: SeverityEnum.Success },
  closeLost: { label: 'Close Lost', severity: SeverityEnum.Warning },
  churned: { label: 'Churned', severity: SeverityEnum.Error },
};

export const ReferralStatusBadge: React.FC<{ status: ReferralDisplayStatus }> = ({ status }) => {
  const meta = REFERRAL_STATUS_META[status] || {
    label: status,
    severity: SeverityEnum.Note,
  };
  return <StatusBadge severity={meta.severity} status={meta.label} />;
};

const fmtDate = (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

/**
 * Who-referred-whom columns, shared by the dashboard and the user-detail tab.
 * `includeReferrer: false` drops the referrer columns for the per-user view,
 * where the referrer is the page itself.
 */
export const buildReferralColumns = (includeReferrer: boolean): ColumnDef<ReferralAdminRow>[] => [
  ...(includeReferrer
    ? ([
        {
          id: 'referrer',
          accessorFn: (row: ReferralAdminRow) =>
            `${row.referrerName || ''} ${row.referrerEmail || ''}`.trim(),
          header: ({ column }) => <DataTableColumnHeader column={column} title="Referred by" />,
          cell: ({ row }) => (
            <div className="min-w-0">
              <Link
                to={`/users/${row.original.referrerUserId}/referrals`}
                className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {row.original.referrerName || row.original.referrerEmail || 'Unknown'}
              </Link>
              <div className="text-xs text-gray-500 dark:text-dark-gray-400 truncate">
                {row.original.referrerEmail}
              </div>
            </div>
          ),
        },
      ] as ColumnDef<ReferralAdminRow>[])
    : []),
  {
    id: 'referred',
    accessorFn: row => `${row.referredName || ''} ${row.referredEmail || ''}`.trim(),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Referred user" />,
    cell: ({ row }) => (
      <div className="min-w-0">
        {row.original.referredUserId ? (
          <Link
            to={`/users/${row.original.referredUserId}`}
            className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {row.original.referredName || row.original.referredEmail || 'Unknown'}
          </Link>
        ) : (
          <span className="font-medium text-gray-900 dark:text-white">
            {row.original.referredName || row.original.referredEmail || 'Unknown'}
          </span>
        )}
        <div className="text-xs text-gray-500 dark:text-dark-gray-400 truncate">
          {row.original.referredEmail}
        </div>
      </div>
    ),
  },
  {
    id: 'clinic',
    accessorFn: row => row.referredClinicName || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Clinic" />,
    cell: ({ row }) =>
      row.original.referredClinicId ? (
        <Link
          to={`/clinics/${row.original.referredClinicId}`}
          className="text-primary-600 dark:text-primary-400 hover:underline"
        >
          {row.original.referredClinicName || `#${row.original.referredClinicId}`}
        </Link>
      ) : (
        <span className="text-gray-400 dark:text-dark-gray-500">—</span>
      ),
  },
  {
    id: 'status',
    accessorFn: row => row.status,
    filterFn: 'equalsString',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => <ReferralStatusBadge status={row.original.status} />,
  },
  {
    id: 'plan',
    accessorFn: row => row.convertedPlan || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Plan" />,
    cell: ({ row }) =>
      row.original.convertedPlan ? (
        <span className="capitalize">{row.original.convertedPlan}</span>
      ) : (
        <span className="text-gray-400 dark:text-dark-gray-500">—</span>
      ),
  },
  {
    id: 'referredAt',
    accessorFn: row => row.referredAt,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Trial started" />,
    cell: ({ row }) => fmtDate(row.original.referredAt),
  },
  {
    id: 'convertedAt',
    accessorFn: row => row.convertedAt || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Converted" />,
    cell: ({ row }) => fmtDate(row.original.convertedAt),
  },
];
